import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ClaimStatusTemplate, ClaimStatusTemplateListResponse } from '@/api/generated/client/models/';

// Status templates are always presented in displayOrder so ordering is consistent everywhere.
const sortByDisplayOrder = (templates: ClaimStatusTemplate[]): ClaimStatusTemplate[] =>
    [...templates].sort((a, b) => a.displayOrder - b.displayOrder);

/**
 * Returns the status templates belonging to the viewer's OWN company.
 */
export function useStatuses(pipelineId?: number) {
    const { data: statuses = [], isLoading } = useQuery<ClaimStatusTemplate[]>({
        queryKey: ['statuses', pipelineId],
        queryFn: async () => {
            const response: ClaimStatusTemplateListResponse = await apiClient.templates.getClaimStatusTemplates({ pipelineId });
            return sortByDisplayOrder(response.templates || []);
        }
    });

    return { statuses, isLoading };
}

/**
 * Returns the status templates belonging to the company that OWNS the given claim.
 *
 * Unlike `useStatuses` (which returns the viewer's own company templates), this resolves
 * status labels correctly for cross-company shared claims, where the claim's
 * `claimStatusTemplateId` belongs to another company. Backed by
 * `GET /api/claims/:claimId/status-templates`, which is authorized for any user with
 * claim access.
 */
export function useClaimStatuses(claimId: number | undefined, pipelineId?: number, enabled = true) {
    const { data: statuses = [], isLoading } = useQuery<ClaimStatusTemplate[]>({
        queryKey: ['claim-statuses', claimId, pipelineId],
        enabled: claimId != null && enabled,
        queryFn: async () => {
            const response: ClaimStatusTemplateListResponse = await apiClient.claims.getStatusTemplatesForClaim({
                claimId: claimId as number,
            });
            return sortByDisplayOrder((response.templates || []).filter((status) => pipelineId === undefined || status.pipelineId === pipelineId));
        },
    });

    return { statuses, isLoading };
}
